// @ts-nocheck
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import CartPromotionIncentiveMessagesState from "@store/Framework/Store/Starter/Data/CartPromotionIncentiveMessages/CartPromotionIncentiveMessagesState";
import { CartPromotionIncentiveMessageModel } from "@store/Framework/Types/ApiModels";
import { Draft } from "immer";

const initialState: CartPromotionIncentiveMessagesState = {
    byCartId: {},
    isLoadingByCartId: {},
    cartLinesFingerprintByCartId: {},
    requestIdByCartId: {},
    hasErrorByCartId: {},
};

// Build a stable representation of the cart inputs that drive promotion eligibility (the cart lines
// and the order total) so we can tell whether the cart changed since the cached promotion messages
// were loaded. The total is included because shipping or promotion recalculations can change it
// without changing the cart lines.
const buildCartFingerprint = (cart: {
    cartLines?: Array<{ id?: string; productId?: string; unitOfMeasure?: string; qtyOrdered?: number }>;
    orderGrandTotal?: number;
}) =>
    `${(cart.cartLines || [])
        .map(line => `${line.id ?? line.productId ?? "?"}:${line.unitOfMeasure ?? ""}:${line.qtyOrdered ?? 0}`)
        .sort()
        .join("|")}#${cart.orderGrandTotal ?? ""}`;

// Each load request gets an id. Invalidating a cart bumps the id so a response from a request that
// was started before the invalidation (and therefore computed for an older cart state) is ignored.
const invalidateInFlightRequest = (draft: Draft<CartPromotionIncentiveMessagesState>, cartId: string) => {
    draft.requestIdByCartId = draft.requestIdByCartId || {};
    draft.requestIdByCartId[cartId] = (draft.requestIdByCartId[cartId] ?? 0) + 1;
};

// When the cart changes, drop any cached incentives/loading state for that cart so the UI does not
// reuse stale promotion messages generated for a previous cart state.
const clearCartCache = (draft: Draft<CartPromotionIncentiveMessagesState>, cartId: string) => {
    delete draft.byCartId?.[cartId];
    delete draft.isLoadingByCartId?.[cartId];
    delete draft.hasErrorByCartId?.[cartId];
    invalidateInFlightRequest(draft, cartId);
};

const actions = {
    "Starter/Data/CartPromotionIncentiveMessages/BeginLoadCartPromotionIncentiveMessages": (
        draft: Draft<CartPromotionIncentiveMessagesState>,
        action: { cartId: string },
    ) => {
        draft.isLoadingByCartId![action.cartId] = true;
        delete draft.hasErrorByCartId?.[action.cartId];
        invalidateInFlightRequest(draft, action.cartId);
    },
    "Starter/Data/CartPromotionIncentiveMessages/CompleteLoadCartPromotionIncentiveMessages": (
        draft: Draft<CartPromotionIncentiveMessagesState>,
        action: { cartId: string; indicators: CartPromotionIncentiveMessageModel[]; requestId?: number },
    ) => {
        // Context/CompleteUpdatingCart is fired for many cart mutations, including note-only edits,
        // but we only want to accept a promotion response for the request that is still current.
        // If the cart changed or the promotions were reset in the meantime, the request id was bumped
        // and the response is stale and must be ignored.
        if (draft.requestIdByCartId?.[action.cartId] !== action.requestId) {
            return;
        }

        draft.byCartId![action.cartId] = action.indicators;
        draft.isLoadingByCartId![action.cartId] = false;
    },
    "Starter/Data/CartPromotionIncentiveMessages/FailedLoadCartPromotionIncentiveMessages": (
        draft: Draft<CartPromotionIncentiveMessagesState>,
        action: { cartId: string; requestId?: number },
    ) => {
        // A failed request is tracked separately and never cached as an (empty) successful result.
        // Ignore failures from requests that were superseded by a newer one or invalidated.
        if (draft.requestIdByCartId?.[action.cartId] !== action.requestId) {
            return;
        }

        draft.isLoadingByCartId![action.cartId] = false;
        draft.hasErrorByCartId = draft.hasErrorByCartId || {};
        draft.hasErrorByCartId[action.cartId] = true;
    },
    "Starter/Data/CartPromotionIncentiveMessages/ClearErrorCartPromotionIncentiveMessages": (
        draft: Draft<CartPromotionIncentiveMessagesState>,
        action: { cartId: string },
    ) => {
        // Lets the widget retry once after a failure (e.g. when it is mounted again) without retrying in a loop.
        delete draft.hasErrorByCartId?.[action.cartId];
    },
    "Data/Carts/CompleteLoadCart": (
        draft: Draft<CartPromotionIncentiveMessagesState>,
        action: {
            model: {
                id: string;
                cartLines?: Array<{ id?: string; productId?: string; unitOfMeasure?: string; qtyOrdered?: number }>;
                orderGrandTotal?: number;
            };
        },
    ) => {
        // Context/CompleteUpdatingCart is too broad: note-only changes also trigger it, but they do not
        // change the promotion eligibility inputs. We therefore key the cache by the cart fingerprint
        // (cart lines and order total) instead of invalidating on every update action. If the fingerprint
        // changes, we drop the old promotion payload so a fresh load is required; if it is unchanged,
        // we keep the cache.
        // This is tracked for every loaded cart (not only the current one) because checkout can target an
        // explicit cart id. A load without the cart lines expanded cannot be compared reliably, so skip it.
        if (!action.model.cartLines) {
            return;
        }

        const cartId = action.model.id;
        const nextFingerprint = buildCartFingerprint(action.model);
        const prevFingerprint = draft.cartLinesFingerprintByCartId?.[cartId];

        if (prevFingerprint !== undefined && prevFingerprint !== nextFingerprint) {
            clearCartCache(draft, cartId);
        }

        draft.cartLinesFingerprintByCartId = draft.cartLinesFingerprintByCartId || {};
        draft.cartLinesFingerprintByCartId[cartId] = nextFingerprint;
    },
    "Data/Promotions/Reset": (draft: Draft<CartPromotionIncentiveMessagesState>) => {
        // Bump the request id of every cart so a request that is still in flight cannot write
        // pre-reset promotion messages back into the cache.
        Object.keys(draft.requestIdByCartId || {}).forEach(cartId => invalidateInFlightRequest(draft, cartId));
        draft.byCartId = {};
        draft.isLoadingByCartId = {};
        draft.hasErrorByCartId = {};
    },
};

const reducer = createTypedReducerWithImmer(initialState, actions);
export type CartPromotionIncentiveMessagesActions = Parameters<typeof reducer>[1];
export default reducer;
